import {
    FilterCategory,
    FilterControlPanel,
    FilterOption,
} from "@jmc/solid-design-system/src/components/organisms/FilterControlPanel/FilterControlPanel";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { RefinementListItem, RefinementListRenderState, useRefinementList } from "react-instantsearch";

import { AvailableFilters } from "./RecipeGrid";
import getTranslation from "./util/getTranslation";

interface PropTypes {
    availableFilters: AvailableFilters;
}

const FACET_MAX_ITEMS = 20; // Maximum number of items to show in the dropdowns

const initializeRefinement = (attribute: string): RefinementListRenderState => {
    return { attribute: attribute, sortBy: ["name:asc"], limit: FACET_MAX_ITEMS };
};

export const RecipeGridFilters = (props: PropTypes): JSX.Element => {
    const { availableFilters } = props;
    const { t } = useTranslation();

    const [filtersState, setFiltersState] = React.useState([{}] as FilterCategory[]);
    const specialtyRefinement = useRefinementList(initializeRefinement("specialty"));
    const courseRefinement = useRefinementList(initializeRefinement("course"));
    const dietaryRefinement = useRefinementList(initializeRefinement("dietary_wishes"));
    const difficultyRefinement = useRefinementList(initializeRefinement("difficulty"));

    const getRefinement = (filterId: string): RefinementListRenderState => {
        let refinement = undefined;

        switch (filterId) {
            case "specialty":
                refinement = specialtyRefinement;
                break;
            case "course":
                refinement = courseRefinement;
                break;
            case "dietary_wishes":
                refinement = dietaryRefinement;
                break;
            case "difficulty":
                refinement = difficultyRefinement;
                break;
            default:
                return;
        }

        return refinement;
    };

    const getRefinementOptions = (category: string, refinement: RefinementListRenderState): FilterOption[] => {
        return (
            refinement?.items?.map((item: RefinementListItem) => {
                const filter = {
                    id: item?.label,
                    label: getTranslation(item?.label, t),
                    value: item?.value,
                    isSelected: item?.isRefined,
                    isRefined: item?.isRefined,
                    count: item.count,
                    category: category,
                };

                return filter;
            }) ?? []
        );
    };

    useEffect(() => {
        const filters = [];
        if (availableFilters.diseases) {
            const filter = {
                id: "specialty",
                name: t("Diseases", { ns: "common" }),
                options: getRefinementOptions("specialty", specialtyRefinement),
            };
            filters.push(filter);
        }
        if (availableFilters.courses) {
            const filter = {
                id: "course",
                name: getTranslation("course", t),
                options: getRefinementOptions("course", courseRefinement),
            };
            filters.push(filter);
        }
        if (availableFilters.dietaryWishes) {
            const filter = {
                id: "dietary_wishes",
                name: getTranslation("dietary_wishes", t),
                options: getRefinementOptions("dietary_wishes", dietaryRefinement),
            };
            filters.push(filter);
        }
        if (availableFilters.levelsOfComplexity) {
            const filter = {
                id: "difficulty",
                name: getTranslation("difficulty", t),
                options: getRefinementOptions("difficulty", difficultyRefinement),
            };
            filters.push(filter);
        }
        setFiltersState(filters);
    }, [specialtyRefinement, courseRefinement, dietaryRefinement, difficultyRefinement]);

    // Handle filter clicks in the dropdowns
    const onChangeFilter = (filter: FilterCategory, filterValue: FilterOption | FilterOption[]): void => {
        const refinement = getRefinement(filter?.id);

        if (Array.isArray(filterValue)) {
            // Handle multiple values. This only happens when we are clearing the filter.
            filterValue.forEach((value) => {
                // In this case, we only refine if isRefined is true, to disable the filter.
                if (value?.isRefined) {
                    refinement?.refine(value?.value);
                }
            });
        } else {
            // Handle single value. This happens when the user clicks on a filter.
            refinement?.refine(filterValue?.value);
        }
    };

    // Handle removal of a tag under the dropdowns
    const onRemoveFilter = (option: FilterOption): void => {
        const refinement = getRefinement(option?.category);
        refinement?.refine(option?.value);
    };

    // Handle clear all filters options from the tag list
    const onResetFilters = (): void => {
        filtersState.forEach((filter) => {
            const refinement = getRefinement(filter?.id);
            filter?.options?.forEach((option) => {
                if (option?.isRefined) {
                    refinement?.refine(option?.value);
                }
            });
        });
    };

    return (
        <div>
            <FilterControlPanel
                filter={filtersState}
                onChangeFilter={onChangeFilter}
                onRemoveFilter={onRemoveFilter}
                onResetFilters={onResetFilters}
                showLabelBelowFilter
                id="RecipeGrid.ControlHeader"
            />
        </div>
    );
};
